import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Zap } from "lucide-react";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const BG =
  "https://images.unsplash.com/photo-1782787543356-84e69567ccd7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njd8MHwxfHNlYXJjaHwzfHxydW5uaW5nJTIwY291cGxlJTIwc3Vuc2V0JTIwb3V0ZG9vcnN8ZW58MHx8fHwxNzg1MTQ1OTc5fDA&ixlib=rb-4.1.0&q=85";

export default function Register() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState({ enabled: true, first_user: false });
  const { login } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    api
      .get("/settings/registration")
      .then(({ data }) => setStatus(data))
      .catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/register", { name, email, password });
      login(data.token, data.user);
      toast.success(
        data.user.role === "admin"
          ? "Konto erstellt – du bist Administrator!"
          : "Konto erstellt!"
      );
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const blocked = !status.enabled && !status.first_user;

  return (
    <div className="min-h-screen relative flex flex-col justify-end bg-[#0A0A0A]">
      <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${BG})` }} />
      <div className="absolute inset-0 bg-gradient-to-t from-black via-black/80 to-black/40" />

      <div className="relative z-10 px-6 pt-16 pb-10 max-w-lg w-full mx-auto">
        <div className="flex items-center gap-2 mb-2">
          <Zap className="w-7 h-7 text-[#CCFF00]" fill="#CCFF00" />
          <span className="font-display font-black text-2xl tracking-tighter">PACELINE</span>
        </div>
        <h1 className="font-display font-black text-5xl tracking-tighter leading-none mb-2">
          Los geht's.
        </h1>
        {status.first_user && (
          <p className="text-[#CCFF00] text-sm font-bold mb-6" data-testid="first-user-hint">
            Du bist der/die Erste – dein Konto wird zum Admin-Konto.
          </p>
        )}

        {blocked ? (
          <div
            data-testid="registration-closed"
            className="bg-[#121212] border border-[#FF3366]/40 rounded-2xl p-6 mt-4"
          >
            <p className="text-[#FF3366] font-bold mb-1">Registrierung geschlossen</p>
            <p className="text-neutral-400 text-sm">
              Die Registrierung ist derzeit deaktiviert. Bitte wende dich an den Administrator.
            </p>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4 mt-4" data-testid="register-form">
            <div>
              <label className="data-label block mb-2">Name</label>
              <input
                data-testid="register-name"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full bg-[#121212] border border-white/10 rounded-2xl px-4 py-4 text-white placeholder-neutral-600 focus:border-[#CCFF00] focus:outline-none transition-colors"
                placeholder="Dein Name"
              />
            </div>
            <div>
              <label className="data-label block mb-2">E-Mail</label>
              <input
                data-testid="register-email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full bg-[#121212] border border-white/10 rounded-2xl px-4 py-4 text-white placeholder-neutral-600 focus:border-[#CCFF00] focus:outline-none transition-colors"
                placeholder="du@beispiel.de"
              />
            </div>
            <div>
              <label className="data-label block mb-2">Passwort (min. 6 Zeichen)</label>
              <input
                data-testid="register-password"
                type="password"
                required
                minLength={6}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full bg-[#121212] border border-white/10 rounded-2xl px-4 py-4 text-white placeholder-neutral-600 focus:border-[#CCFF00] focus:outline-none transition-colors"
                placeholder="••••••••"
              />
            </div>
            <button
              data-testid="register-submit"
              type="submit"
              disabled={loading}
              className="w-full bg-[#CCFF00] text-black font-display font-black text-lg rounded-full py-4 active:scale-95 transition-transform disabled:opacity-60"
            >
              {loading ? "Erstellen..." : "Konto erstellen"}
            </button>
          </form>
        )}

        <p className="text-center text-neutral-400 mt-6 text-sm">
          Schon dabei?{" "}
          <Link to="/login" data-testid="go-login" className="text-[#CCFF00] font-bold">
            Einloggen
          </Link>
        </p>
      </div>
    </div>
  );
}
